<template>
  <div class="min-h-screen relative">
    <TopMenu :username="username" @info="handleClearCache" />
    <div
      v-if="isShowClearCacheModal"
      class="fixed top-0 left-0 flex justify-center items-center w-full h-full bg-modal z-50"
    >
      <ClearCacheModal
        @close="isShowClearCacheModal = false"
        @clearCache="toggleClearCacheModal"
      />
    </div>

    <div
      v-if="showUpdateWidgetModal"
      class="fixed top-0 left-0 flex justify-center items-center w-full h-full bg-modal z-50"
    >
      <UpdateWidgetModal
        @discard-changes="discardChanges"
        @return-to-editing="returnToEditing"
      />
    </div>

    <div class="flex w-full">
      <aside
        class="flex flex-col justify-between min-w-48 mt-[76px] py-10 bg-neutral-100"
      >
        <LeftMenu />
        <p class="self-center text-white">Version {{ version }}</p>
      </aside>

      <main
        ref="mainContainer"
        class="flex flex-col w-full mt-[76px] px-8 pt-8 bg-neutral-0 overflow-y-scroll scroll-smooth"
        :class="isShowClearCacheModal ? '!overflow-y-hidden' : 'overflow-auto'"
        style="height: calc(100vh - 76px)"
      >
        <ToastPopup
          v-if="showClearCacheToast"
          status="success"
          :text="toastText"
          :width="300"
          @close="showClearCacheToast = false"
        />

        <router-view />

        <footer class="self-end mt-auto mb-8">
          <a href="https://www.resrequest.com/" target="_blank">
            <img src="../../assets/footer-logo.png" alt="footer logo" class="w-[100px] h-8 opacity-75" />
          </a>
        </footer>
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from "vue";
import { useStore } from 'vuex';
import axios from "axios";
import TopMenu from "../menus/TopMenu.vue";
import LeftMenu from "../menus/LeftMenu.vue";
import ToastPopup from "@/components/notifications/ToastPopup.vue";
import ClearCacheModal from "@/components/modals/ClearCacheModal.vue";
import UpdateWidgetModal from "../modals/UpdateWidgetModal.vue";

const API_URL = process.env.VUE_APP_API;

const username = ref<string | null>(null);
const version = ref<string>('');

const getVersion = async () => {
  try {
    const { data } = await axios.get(`${API_URL}v3/fetchVersion`);
    version.value = data.version + '.' + data.build;
  } catch (error) {
    console.error(error);
  }
};

onMounted(async () => {
  username.value = sessionStorage.getItem("username");
  await getVersion();
});

const toastText = ref<string>('');
const showClearCacheToast = ref<boolean>(false);
const isShowClearCacheModal = ref<boolean>(false);
const startClearCache = ref<boolean>(false);

const toggleClearCacheModal = (info: string) => {
  if (info !== undefined) {
    showClearCacheToast.value = true;
    toastText.value = info;
    setTimeout(() => { showClearCacheToast.value = false }, 3000);
  }

  isShowClearCacheModal.value = !isShowClearCacheModal.value;
}

const store = useStore();
const showUpdateWidgetModal = ref<boolean>(false);
const isSaveBtnVisible = computed(() => store.getters.getSaveBtnVisibility);

const handleClearCache = () => {
  startClearCache.value = true;
}

watch(() => [startClearCache.value, isSaveBtnVisible.value], ([valueOne, valueTwo]) => {
  if (valueOne && valueTwo) {
    showUpdateWidgetModal.value = true;
    startClearCache.value = false;
    return;
  }

  if (valueOne) {
    isShowClearCacheModal.value = true;
    startClearCache.value = false;
  }
});

const discardChanges = () => {
  showUpdateWidgetModal.value = false;
  isShowClearCacheModal.value = true;
}

const returnToEditing = () => {
  showUpdateWidgetModal.value = false;
  isShowClearCacheModal.value = false;
}
</script>
